import { CalendarClock, ClipboardCheck, FileText, Menu, Package } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { brandColors } from "config/colors.config";
import HeaderBadge from "components/header-badge/header-badge";
import type { MenuItem } from "./DashboardLayout";

// The bar's pages, left to right; «Meniu» always comes last. A page borrows its address,
// the number on it and the pages that highlight it from the sidebar item with the same id
// (see Menu.js), so the bar and the sidebar cannot disagree. A page whose sidebar item is
// not in the list is left out. A slot with a `countNameKey` is announced with its number,
// as that text says it ("Tasks: 3 waiting"), instead of the label and a bare digit.
const PAGE_SLOTS: {
  menuItemId: string;
  labelKey: string;
  countNameKey?: string;
  Icon: LucideIcon;
}[] = [
  {
    menuItemId: "cab-tasks",
    labelKey: "tasks",
    countNameKey: "header_my_work_title",
    Icon: ClipboardCheck,
  },
  { menuItemId: "cab-leaves", labelKey: "profile_leaves_tab", Icon: CalendarClock },
  { menuItemId: "cab-assets", labelKey: "cabinet_assets", Icon: Package },
  { menuItemId: "cab-docs", labelKey: "documents", Icon: FileText },
];

interface PhoneBottomBarProps {
  /** The sidebar items this person may see. */
  menuItems: MenuItem[];
  /** The sidebar item that matches the current address, if any. */
  activeItemId: MenuItem["id"] | null;
  /** The sidebar drawer is open: «Meniu» is the one highlighted. */
  isMenuOpen: boolean;
  onOpenMenu: () => void;
  onNavigate: (url: string) => void;
}

interface BarEntry {
  key: string;
  label: string;
  Icon: LucideIcon;
  isActive: boolean;
  /** The number shown on the icon, for «Užduotys». */
  badge?: number;
  /** What a screen reader says when the number is shown, instead of the label. */
  nameWithCount?: string;
  onClick: () => void;
}

/** The phone's bottom bar: four cabinet pages and the button that opens the sidebar. */
export function PhoneBottomBar({
  menuItems,
  activeItemId,
  isMenuOpen,
  onOpenMenu,
  onNavigate,
}: PhoneBottomBarProps) {
  const { t } = useTranslation();

  const entries: BarEntry[] = [];
  for (const { menuItemId, labelKey, countNameKey, Icon } of PAGE_SLOTS) {
    const item = menuItems.find(({ id }) => id === menuItemId);
    if (!item) continue;
    entries.push({
      key: menuItemId,
      label: t(labelKey),
      Icon,
      isActive: !isMenuOpen && activeItemId === item.id,
      badge: item.badge,
      nameWithCount:
        countNameKey && item.badge ? t(countNameKey, { count: item.badge }) : undefined,
      onClick: () => onNavigate(item.url),
    });
  }
  entries.push({
    key: "menu",
    label: t("phone_bar_menu"),
    Icon: Menu,
    isActive: isMenuOpen,
    onClick: onOpenMenu,
  });

  // z-30 keeps the bar under the drawer's dimmed backdrop (z-40), so a tap outside the drawer still closes it.
  return (
    <nav
      aria-label={t("phone_bar_label")}
      className="fixed inset-x-0 bottom-0 z-30 border-t border-gray-200 bg-white pb-[env(safe-area-inset-bottom)]"
    >
      <ul className="flex h-16">
        {entries.map(({ key, label, Icon, isActive, badge, nameWithCount, onClick }) => (
          <li key={key} className="flex-1">
            <button
              type="button"
              onClick={onClick}
              aria-current={isActive ? "true" : undefined}
              aria-label={nameWithCount}
              className={`flex h-full w-full flex-col items-center justify-start gap-1 border-none pt-2 bg-transparent text-xs outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gray-400 ${
                isActive ? "font-semibold" : "text-gray-500"
              }`}
              style={isActive ? { color: brandColors.primary.base } : undefined}
            >
              <span className="relative">
                <Icon className="h-5 w-5" />
                {badge ? <HeaderBadge count={badge} /> : null}
              </span>
              <span className="line-clamp-2 max-w-full break-words px-0.5 text-center leading-tight">{label}</span>
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}
